/**
 * Semantic color tokens - purpose-based colors that reference primitives
 * These define the meaning/usage of colors in the application
 */

import { primitiveColors, primitiveGradients } from './primitives';

export const semanticColors = {
  // Brand colors
  primary: primitiveColors.blue1, // Light blue background used for primary highlights
  brandPrimary: primitiveColors.blue2, // Actual brand blue for links and CTAs
  brandPrimaryHover: primitiveColors.blue9, // Hover state for brand primary buttons
  brandAccent: primitiveColors.logoPurple, // Logo purple — control accent (toggle/CTA hover)
  accent: primitiveColors.red1,

  // Backgrounds
  background: primitiveColors.white,
  backgroundSecondary: primitiveColors.blue1,
  backgroundTertiary: primitiveColors.rockSlate,
  surface: primitiveColors.white,
  surfaceElevated: primitiveColors.white2,
  surfaceSubtle: primitiveColors.gray21, // Very light hover/subtle bg
  surfaceWarm: primitiveColors.gray23, // Card/paper backgrounds
  surfaceNeutral: primitiveColors.gray24, // Neutral gray surface
  surfaceLight: primitiveColors.gray27, // Light surface variant

  // Text colors
  textPrimary: primitiveColors.rockSlate,
  textSecondary: primitiveColors.gray10,
  textBody: primitiveColors.gray11, // General body text (#333)
  textTertiary: primitiveColors.gray13, // Medium gray text (#666)
  textCaption: primitiveColors.gray14, // Caption/icon text (#6B7280)
  textSubheadline: primitiveColors.gray15, // Subheadline text (#6B6C6E)
  textFooter: primitiveColors.gray16, // Footer/fine print text
  textMuted: primitiveColors.gray8,
  textDisabled: primitiveColors.gray9,
  textOnPrimary: primitiveColors.white,
  textPlaceholder: primitiveColors.gray31, // Placeholder text (#767676, WCAG AA contrast on white)
  // Brand blue for TEXT on a light surface. `brandPrimary` (#5B6CFF) is 4.17:1
  // on white — fine for large text, icons, and focus rings (3:1), short of the
  // 4.5:1 that body-size text needs. This one is 5.31:1 and still reads as the
  // brand blue, so small brand-colored labels have a token that passes.
  textBrand: primitiveColors.blue9,
  textDimmed: primitiveColors.gray32, // Dimmed text (#7e7f88)

  // Interactive states
  hover: primitiveColors.rockSlate, // Dark button hover background
  focus: primitiveColors.blue1,

  // Borders
  border: primitiveColors.gray7,
  borderPrimary: primitiveColors.blue1,
  borderLight: primitiveColors.gray17, // Light border (#E0E0E0)
  borderMedium: primitiveColors.gray18, // Medium border (#CCCCCC)

  // Input states
  inputBorder: primitiveColors.gray25, // Default input border
  inputBorderHover: primitiveColors.gray26, // Input border on hover
  inputBorderFocus: primitiveColors.black, // Input border on focus
  composerBorder: 'rgba(12, 47, 55, 0.08)', // Hairline edge on the floating chat composer

  // Disabled
  disabled: primitiveColors.gray20, // Disabled text (#B3B3B3)
  disabledBackground: primitiveColors.gray17, // Disabled background

  // Status colors
  success: primitiveColors.blue1, // Used as success highlight background (light blue)
  successAction: primitiveColors.green1, // Success action button background (e.g. Copied state)
  successBackground: primitiveColors.green2, // Light green chip/badge background
  warning: primitiveColors.red2,
  warningIndicator: primitiveColors.amber1, // Amber dot for unsaved-changes indicators
  warningBackground: primitiveColors.amber2, // Light amber surface for notice/warning banners
  warningBannerBorder: primitiveColors.amber3, // Amber border for notice/warning banners
  warningBannerIcon: primitiveColors.amber3, // Amber icon color for notice/warning banners
  warningText: '#7A4F00', // Dark amber-brown text — 5.4:1 on warningBackground
  infoBackground: primitiveColors.purple6, // Light purple surface for info banners
  infoBorder: primitiveColors.logoPurple, // Purple border for info banners
  infoText: primitiveColors.logoPurple, // Purple text for info banners
  error: primitiveColors.red1,
  info: primitiveColors.blue2,
  danger: primitiveColors.red5, // Destructive actions (#ef4444)
  dangerHover: primitiveColors.red8, // Hover state for danger/destructive buttons (#dc2626)
  dangerStrong: primitiveColors.red6, // Hard error borders (#FF0000)
  errorOnDark: primitiveColors.red10, // Error text on dark surfaces (#FFB3B3)

  // Link
  link: primitiveColors.blue2, // Hyperlink color
  websiteLink: primitiveColors.gray28, // Neutral gray for website link icon + border

  // Chat-specific
  chatBotBackground: primitiveColors.gray2,
  chatBotMessage: primitiveColors.blue5, // Chat bot message background
  chatBotMessageHover: primitiveColors.blue6, // Chat bot message hover

  // Source type icon colors
  sourceGovernment: primitiveColors.blue4, // Government source icon
  sourceNews: primitiveColors.orange2, // News source icon
  sourceCurated: primitiveColors.purple4, // Curated source icon

  // Ballot
  ballotPreviewText: primitiveColors.red2,
  ballotElectionCard: primitiveColors.rockSlate, // Election/tab header dark surface (rock slate)
  ballotLevelHeader: primitiveColors.rockSlate, // Federal/State/Local group header bg (rock slate)
  ballotOfficeHeader: primitiveColors.gray28, // Office/contest header gray (e.g. "U.S. Senate")
  ballotpediaBlue: primitiveColors.ballotpediaBlue, // Ballotpedia logo blue (the "B")
  ballotpediaOrange: primitiveColors.ballotpediaOrange, // Ballotpedia logo orange (the "P")
  themeAqua: primitiveColors.aqua1, // Aqua accent color

  // Overlay variants
  overlay: primitiveColors.overlay,
  overlayDark: primitiveColors.overlayDark,
  overlaySubtle: 'rgba(0, 0, 0, 0.04)', // Hover tint on light backgrounds
  overlaySelected: 'rgba(0, 0, 0, 0.08)', // Selected-row tint (2× hover) over light backgrounds
  overlayWhite08: 'rgba(255, 255, 255, 0.08)', // Ghost button hover fill on dark surfaces
  overlayWhite10: 'rgba(255, 255, 255, 0.1)', // Subtle white on dark
  overlayWhite12: 'rgba(255, 255, 255, 0.12)', // Subtle border on dark surfaces
  overlayWhite15: 'rgba(255, 255, 255, 0.15)', // Divider rule on dark surfaces
  overlayWhite25: 'rgba(255, 255, 255, 0.25)', // Ghost control keyline on dark surfaces
  overlayWhite30: 'rgba(255, 255, 255, 0.3)', // Ghost button border on dark surfaces
  overlayWhite50: 'rgba(255, 255, 255, 0.5)', // Semi-transparent white
  overlayWhite60: 'rgba(255, 255, 255, 0.6)', // Ghost button border hover on dark surfaces
  overlayWhite70: 'rgba(255, 255, 255, 0.7)', // Secondary text on dark surfaces (~9:1 on rockSlate)
  overlayWhite85: 'rgba(255, 255, 255, 0.85)', // Ghost button text on dark surfaces
  overlayWhite90: 'rgba(255, 255, 255, 0.9)', // Near-opaque white panels
  overlayWhite95: 'rgba(255, 255, 255, 0.95)', // Almost-opaque white panels
  saveBarBackground: 'rgba(15, 23, 42, 0.94)', // Dark navy frosted-glass save bar
  shadowLight: 'rgba(0, 0, 0, 0.1)', // Common box-shadow alpha
  shadowMedium: 'rgba(0, 0, 0, 0.25)', // Medium box-shadow alpha

  // Text emphasis (Material Design alpha levels on black)
  textHighEmphasis: 'rgba(0, 0, 0, 0.9)', // High-emphasis body text
  textMediumEmphasis: 'rgba(0, 0, 0, 0.6)', // Medium-emphasis secondary text
} as const;

export const semanticGradients = {
  accountAvatar: primitiveGradients.accountAvatar,
  primary: primitiveGradients.primary,
  rainbow: primitiveGradients.rainbow,
  borderGradient: primitiveGradients.borderGradient,
  borderGradientDisabled: primitiveGradients.borderGradientDisabled,
  navBorderGradient: primitiveGradients.navBorderGradient,
} as const;
